import React, {forwardRef, useImperativeHandle, useState} from "react";
import {useGlobalNotificationMessage} from "../context/message";
import {useStripePayments} from "../context/stripe";
import {usePaymentService} from "../services/payment-service";
import {Modal} from "react-bootstrap";
import Section from "./section";
import HelperService from "../services/helper-service";
import {AppAttendanceChangedType, AppEvents, AppSessionKeys} from "../utils/constants";
import {useEventService} from "../services/event-service";
import {useNotifyEventsService} from "../services/notify-events-service";
import Loading from "./loading";
import {setSessionData} from "../modules/session-persistor";


const StripePaymentModal = forwardRef( ({ ...props}, ref) => {
    const [submitting, setSubmitting] = useState(false);
    const [show, setShow] = useState(false);
    const [paymentData, setPaymentData] = useState({});
    const {addErrorMessage, setMessage} = useGlobalNotificationMessage();
    const {initStripe} = useStripePayments();

    const ps = usePaymentService();
    const es = useEventService();
    const nes = useNotifyEventsService();

    //expose api for interacting with component
    useImperativeHandle(ref, () => ({
        openModal: openModal,
        clearModal: clearModal,
        closeModal: closeModal,
    }));

    const openModal = ({attendanceType, eventId, message}) => {
        setPaymentData({
            message: message,
            attendanceType: attendanceType,
            eventId: eventId,
        })
        setShow(true);
    }

    const clearModal = () => {
        setPaymentData({})
        setShow(false);
    }

    const closeModal = () => setShow(false);

    const createAndConfirmPaymentIntent = () => {
        setSubmitting(true);
        //create paymentIntent
        ps.createEventPaymentIntent({eventId: paymentData.eventId, attendanceType: paymentData.attendanceType})
            .then((res) => {

                if (!res.stripeAccount || !res.paymentIntent?.id || !res.paymentIntent.client_secret) {
                    throw new Error('Payment failed. Intent did not have all necessary information.');
                }

                //init stripe
                initStripe(res?.paymentIntent?.client_secret, res.stripeAccount)
                    .then(async (newStripe)=>{
                        //confirm paymentIntent
                        const result = await newStripe.confirmCardPayment(res?.paymentIntent?.client_secret);
                        if (result?.error) {
                            // Show error to your customer (e.g., insufficient funds)
                            throw new Error(result.error.message);
                        } else {
                            if (result.paymentIntent.status === 'requires_capture') {
                                //finish booking
                                es.createAttendance({eventId: paymentData.eventId, type: paymentData.attendanceType, paymentIntentId: res?.paymentIntent.id})
                                    .then(async (res) => {
                                        nes.trigger(AppEvents.attendanceChanged, {eventId: paymentData.eventId, type: AppAttendanceChangedType.booking});
                                        setMessage({
                                            message: 'Your registration is confirmed with the FitPro and you will get all class notifications.',
                                            status: 'success',
                                            title: 'Booking confirmed',
                                            closeBtnShow: true,
                                            closeBtnClassName: 'btn btn-primary',
                                            closeBtnText: 'Ok',
                                        });
                                        closeModal();
                                        setSubmitting(false);
                                    })
                                    .catch(error => {
                                        HelperService.handleApiErrors(error, addErrorMessage);
                                        setSubmitting(false);
                                    })

                            } else {
                                addErrorMessage('We tried to alocate funds but stripe responded with unexpected status, thus we cannot continue: '+result?.paymentIntent?.status);
                            }
                        }

                    })
                    .catch((error) => {
                        HelperService.handleApiErrors(error, addErrorMessage);
                        setSubmitting(false);
                    })

            })
            .catch(error => {
                HelperService.handleApiErrors(error, addErrorMessage);
                setSubmitting(false);
            })
    }

    const showPaymentMethods = ()=>{
        setSessionData(AppSessionKeys.continueWithBooking, paymentData);
        nes.trigger(AppEvents.showPaymentMethods, paymentData.eventId);
    };

    return (
        <>
            <Modal size='md' show={!!show} onHide={closeModal} centered>
                <Modal.Header closeButton>
                    <h4 className="header-stripe-blue pb-1">Payment confirmation</h4>
                </Modal.Header>
                <Modal.Body className="p-5">
                    <Section animation={HelperService.getRandomAnimation()}>
                        <div className="row pb-3">
                            <div className="col-sm-12 mb-3">
                                <p>{paymentData?.message}</p>
                            </div>
                            <div className="col-sm-12 text-center mb-3">
                                {!submitting && <button className="btn btn-primary" onClick={()=>createAndConfirmPaymentIntent()}>Confirm</button>}
                                {!submitting && <button className="ms-2 btn btn-outline-primary" onClick={()=>showPaymentMethods()}>Manage payment methods</button>}
                                {submitting && <Loading />}
                            </div>
                        </div>
                    </Section>
                </Modal.Body>
            </Modal>
        </>
    )
});

export default StripePaymentModal;